import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import FireIcon from '@mui/icons-material/LocalFireDepartment';

interface StreakData {
  current_streak: number;
  best_streak: number;
  total_on_time: number;
  total_sessions: number;
  milestone: {
    days: number;
    message: string;
    type: string;
  } | null;
  streak_break: {
    previous_streak: number;
    message: string;
  } | null;
}

interface StreakBadgeProps {
  streak: StreakData | null;
  compact?: boolean;
}

/**
 * Documentation streak badge component.
 * Shows the therapist's current documentation streak (consecutive days
 * with all notes signed within 48 hours of the session).
 *
 * Appears in the header next to the briefing pill and inside the
 * Today's Briefing panel.
 */
function StreakBadge({ streak, compact = false }: StreakBadgeProps) {
  if (!streak || (streak.current_streak === 0 && !streak.streak_break)) {
    return null;
  }

  const { current_streak, best_streak, streak_break } = streak;

  const tooltipContent = current_streak > 0
    ? `${current_streak}-day on-time documentation streak! Best: ${best_streak} days.`
    : streak_break?.message || 'Start your documentation streak today!';

  if (compact) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: '12px',
            bgcolor: current_streak > 0 ? 'warning.light' : 'grey.100',
            cursor: 'default',
          }}
        >
          <FireIcon
            sx={{
              fontSize: 14,
              color: current_streak > 0 ? 'warning.main' : 'grey.400',
            }}
          />
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              color: current_streak > 0 ? 'warning.dark' : 'grey.500',
              lineHeight: 1,
            }}
          >
            {current_streak > 0 ? current_streak : '0'}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: current_streak > 0 ? 'warning.light' : 'grey.50',
        border: 1,
        borderColor: current_streak > 0 ? 'warning.main' : 'grey.200',
      }}
    >
      <FireIcon
        sx={{
          fontSize: 20,
          color: current_streak > 0 ? 'warning.main' : 'grey.400',
        }}
      />
      <Box sx={{ flex: 1 }}>
        {current_streak > 0 ? (
          <>
            <Typography variant="body2" fontWeight={600}>
              {current_streak}-day streak
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All notes signed within 48hrs
            </Typography>
          </>
        ) : streak_break ? (
          <>
            <Typography variant="body2" fontWeight={500}>
              {streak_break.message}
            </Typography>
          </>
        ) : null}
      </Box>
      {best_streak > current_streak && best_streak > 0 && (
        <Typography variant="caption" color="text.secondary">
          Best: {best_streak}
        </Typography>
      )}
    </Box>
  );
}

export default StreakBadge;
